// Racket Lab — Calendar mini-widget (R4.1-B, Feb 2026).
//
// Read-only list of the next N days of internal scheduling events tied to
// Racket Lab tickets. Sourced from the global `calendar_events` collection
// via /api/racket-lab/calendar/upcoming — Racket Lab does NOT own a
// parallel calendar.
//
// RBAC scoping is enforced server-side: admin sees all, technician sees
// own tickets, player sees own tickets. Card click → opens the ticket
// detail (uses the existing /app/racket-lab/tickets?ticket=ID convention).

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CalendarDays, MapPin, Ticket as TicketIcon, RefreshCw } from "lucide-react";
import RacketLabNav from "@/components/racket_lab/RacketLabNav";

const _fmt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return iso;
  }
};

const _featureDisabled = (err) =>
  err?.response?.status === 404 &&
  err?.response?.data?.detail === "errors.racket_lab_disabled";

export default function RacketLabCalendar() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [horizonDays] = useState(14);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/racket-lab/calendar/upcoming?days=${horizonDays}`);
      setItems(r.data?.items || []);
    } catch (err) {
      if (_featureDisabled(err)) {
        setDisabled(true);
        return;
      }
      toast.error(t("racketLab.tickets.updateFail"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (disabled) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="racket-lab-calendar-disabled">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <p className="font-semibold text-amber-900">
            {t("racketLab.tickets.disabledTitle")}
          </p>
          <p className="text-sm text-amber-800 mt-1">
            {t("racketLab.tickets.disabledBody")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="max-w-5xl mx-auto space-y-5"
      data-testid="racket-lab-calendar-page"
    >
      <RacketLabNav currentUser={user} t={t} />

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-violet-600" />
            {t("racketLab.calendar.pageTitle")}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t("racketLab.calendar.pageSub", { days: horizonDays })}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="racket-lab-calendar-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5 me-1.5" />
          {t("racketLab.tickets.refresh")}
        </Button>
      </div>

      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">
          …
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500"
          data-testid="racket-lab-calendar-empty"
        >
          {t("racketLab.calendar.empty")}
        </div>
      ) : (
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          data-testid="racket-lab-calendar-list"
        >
          {items.map((it) => (
            <li key={it.event_id}>
              <button
                type="button"
                onClick={() => navigate(`/app/racket-lab/tickets?ticket=${it.ticket_id}`)}
                className="w-full text-start rounded-xl border border-slate-200 bg-white p-4 hover:border-violet-300 hover:shadow-sm transition"
                data-testid={`racket-lab-calendar-item-${it.event_id}`}
              >
                <div className="text-[11px] text-violet-700 uppercase tracking-wide font-semibold mb-1">
                  {t("racketLab.calendar.itemBadge")}
                </div>
                <div className="text-sm font-medium text-slate-900">
                  {_fmt(it.starts_at)} → {_fmt(it.ends_at)?.slice(11, 16)}
                </div>
                {it.location && (
                  <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {it.location}
                  </div>
                )}
                <div className="mt-2 text-xs text-slate-700">
                  <span className="font-medium">{it.racket_model || "—"}</span>
                  {it.sport && <span className="text-slate-500"> · {it.sport}</span>}
                </div>
                {it.counterpart_name && (
                  <div className="mt-1 text-[11px] text-slate-500">
                    {it.counterpart_role === "player"
                      ? t("racketLab.calendar.forPlayer")
                      : t("racketLab.calendar.byTech")}
                    : {it.counterpart_name}
                  </div>
                )}
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-700">
                  <TicketIcon className="h-3 w-3" />
                  {t("racketLab.calendar.openTicket")}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
